import { h, Fragment } from 'preact'
import { useEffect, useState, useRef } from 'preact/compat'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { PAYMENT_METHOD_TYPES } from '../../../constants/paymentMethods'
import { useTranslations } from '../../../hooks/useTranslations'
import DirectDebitIcon from '../../../assets/icons/direct-debit-white.svg'
import { redirect, isMobile } from '../../../helpers/common'
import { DataDog } from '../../../datadog'
import {
  dispatchMFEAdobeEvent,
  dispatchMFEEvent,
} from '../../../helpers/events'
import { paymentResetCardEncryptedData } from '../../../redux/slice/paymentSlice'
import ErrorMessage from '../../Common/ErrorMessage'
import InfoMessage from '../../Common/InfoMessage'
import { directDebitRequest } from '../../../api/directDebit'
import Loader from '../../Common/Loader/index'
import { RESPONSE_ERROR_TEXT } from '../../../helpers/serverTextResponses'
import {
  ERROR_RESPONSES,
  SUCCESS_RESPONSES,
} from '../../../constants/serverResponses'
import Modal from '../../Common/Modal'
import useFailureRedirect from '../../../hooks/useFailureRedirect'
import useAuthoriseError from '../../../hooks/useAuthoriseError'
import { pageStateSelector } from '../../../redux/slice/pageStateSlice'
import { transactionDetailsSelector } from '../../../redux/slice/transactionDetailsSlice'
import { prepareMetadata } from '../../../hooks/useMetadata'
import './styles.scss'

export const REDIRECTION_MODE = {
  MODAL: 'MODAL',
  REDIRECT: 'REDIRECT',
}

function DirectDebit({
  redirectMode = REDIRECTION_MODE.MODAL,
  showPaymentConfirmation,
  handleSubmit,
  loaderImage,
  errors,
}) {
  const l10n = useTranslations()
  const transactionDetails = useSelector(transactionDetailsSelector)
  const pageState = useSelector(pageStateSelector)
  const { directDebitDate, successUrl, metadata } = transactionDetails
  const { isPaymentBlocked } = pageState
  const [isPaymentPending, setIsPaymentPending] = useState(false)
  const [error, setError] = useState('')

  const [isModalOpened, setModalState] = useState(false)
  const [directDebitModalUrl, setDirectDebitModalUrl] = useState('')
  const iframeLoadTimeoutRef = useRef(null)

  const closeModal = () => {
    setModalState(false)
    setDirectDebitModalUrl('')
    if (iframeLoadTimeoutRef.current) {
      clearTimeout(iframeLoadTimeoutRef.current)
      iframeLoadTimeoutRef.current = null
    }
  }

  useEffect(() => {
    const onMessage = e => {
      switch (e.data) {
        case 'cancel': {
          closeModal()
          return
        }
        case 'success': {
          DataDog.info(
            `Redirection. Component: DirectData (onMessage), redirect url: ${successUrl}`,
          )
          closeModal()
          redirect(successUrl)
          return
        }
        case 'error': {
          setError(
            RESPONSE_ERROR_TEXT(l10n, ERROR_RESPONSES.USER_DETAILS_ERROR),
          )
          closeModal()
        }
      }
    }

    const urlParams = new URLSearchParams(window.location.search)
    const hasErrorFromUrl = urlParams.get('hasDDError')

    if (hasErrorFromUrl) {
      setError(RESPONSE_ERROR_TEXT(l10n, ERROR_RESPONSES.USER_DETAILS_ERROR))
    }

    window.addEventListener('message', onMessage)

    return () => {
      window.removeEventListener('message', onMessage)
      if (iframeLoadTimeoutRef.current) {
        clearTimeout(iframeLoadTimeoutRef.current)
        iframeLoadTimeoutRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!isPaymentBlocked) {
      setError('')
    }
  }, [isPaymentBlocked])

  useFailureRedirect()
  const handleAuthoriseError = useAuthoriseError()

  const checkIsTermsAndConditionsEnabled = () => {
    if (isPaymentBlocked) {
      setError(l10n.t('payment-btn-error'))
      return false
    }
    setError('')
    return true
  }

  const handlePayment = async () => {
    DataDog.info('DirectDebit payment button clicked')
    dispatchMFEEvent('tui-button.payment-btn-clicked', {
      paymentMethod: 'DirectDebit',
    })
    dispatchMFEAdobeEvent('Pay-DirectDebit')

    if (isPaymentBlocked) {
      setError(l10n.t('payment-btn-error'))
    }

    handleSubmit(async ({ notes, directDebitEmail }) => {
      if (!checkIsTermsAndConditionsEnabled()) {
        return
      }

      const continuePayment = async () => {
        setIsPaymentPending(true)
        const { responseData, responseStatus } = await directDebitRequest({
          DirectDebitWithdrawalDay: directDebitDate,
          cancelUrl: window.location.href,
          metadata: prepareMetadata(metadata),
          accountHolderEmail: directDebitEmail,
          notes,
        })

        if (responseStatus === SUCCESS_RESPONSES.OK) {
          window.tuiPaymentsMFEInstance?.onAuthorised?.()
          setIsPaymentPending(false)

          switch (redirectMode) {
            case REDIRECTION_MODE.MODAL: {
                setDirectDebitModalUrl('')
                setModalState(true)
                if (iframeLoadTimeoutRef.current) {
                  clearTimeout(iframeLoadTimeoutRef.current)
                }
                iframeLoadTimeoutRef.current = setTimeout(() => {
                  setDirectDebitModalUrl(responseData.redirectUrl)
                }, 50)
                if (iframeLoadTimeoutRef.current) {
                  clearTimeout(iframeLoadTimeoutRef.current)
                }
                iframeLoadTimeoutRef.current = setTimeout(() => {
                  setDirectDebitModalUrl(responseData.redirectUrl)
                }, 50)
              return
            }
            case REDIRECTION_MODE.REDIRECT: {
              DataDog.info(
                `Redirection. Component: DirectDebit, redirect url: ${responseData.redirectUrl}`,
              )
              redirect(responseData.redirectUrl)
              return
            }
          }

          return
        }

        handleAuthoriseError(responseStatus, responseData)

        if (responseData?.isRetryable) {
          setError(RESPONSE_ERROR_TEXT(l10n, responseStatus))
        }

        setIsPaymentPending(false)
      }

      const mfeElement = window.tuiPaymentsMFEInstance

      if (mfeElement?.onAuthorising) {
        DataDog.info(`onAuthorising hook has been enabled`)

        mfeElement
          .onAuthorising?.({ paymentMethod: PAYMENT_METHOD_TYPES.DirectDebit })
          .then(async () => {
            DataDog.info(`onAuthorising Promise resolved`)
            await continuePayment()
          })
          .catch(() => {
            console.log('onAuthorising Promise rejected')
            DataDog.info(`onAuthorising Promise rejected`)
          })
      } else {
        await continuePayment()
      }
    })()
  }

  const modalClasses = classNames('updated-dd-modal', { desktop: !isMobile() })

  return (
    <>
      <div className="direct-debit-wrapper">
        <ErrorMessage
          isShown={!!errors?.directDebitEmail}
          message={l10n.t('FIELDS_ERROR')}
          data-testid="payment-error"
        ></ErrorMessage>
        {showPaymentConfirmation && (
          <InfoMessage
            message={l10n.t(
              "You're about to set up your Direct Debit powered by Trustly",
            )}
            data-testid="direct-debit-info-container"
            className="direct-debit-info-container"
          />
        )}

        <ErrorMessage
          isShown={Boolean(error)}
          message={error}
          data-testid="direct-debit-error"
        />

        {isPaymentPending ? (
          <Loader loaderImage={loaderImage} />
        ) : (
          <div className="direct-debit-button">
            <button
              type="button"
              className="button primary medium"
              data-testid="direct-debit-button"
              onClick={handlePayment}
            >
              {l10n.t('Pay With')}
              <img
                src={DirectDebitIcon}
                alt={l10n.t('Direct Debit logo')}
                className="direct-debit-button__logo"
              />
            </button>
          </div>
        )}
      </div>
      <Modal
        isOpened={isModalOpened}
        withControlls={false}
        onCancel={closeModal}
        withControls={false}
        className={modalClasses}
      >
          <div className="modalContent" data-testid="dd-modal">
          <iframe
            className="dd-modal__container"
            scrolling="no"
            id="test-iframe"
            width="100%"
            height="625"
            src={directDebitModalUrl}
            onLoad={() => {
              if (iframeLoadTimeoutRef.current) {
                clearTimeout(iframeLoadTimeoutRef.current)
                iframeLoadTimeoutRef.current = null
              }
            }}
          />
        </div>
      </Modal>
    </>
  )
}

DirectDebit.propTypes = {
  redirectMode: PropTypes.oneOf(Object.values(REDIRECTION_MODE)),
  showPaymentConfirmation: PropTypes.bool,
  handleSubmit: PropTypes.func,
  loaderImage: PropTypes.string,
}

export default DirectDebit
